import Toolbar from "@mui/material/Toolbar";
import {
  Divider,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import {
  darkGrey,
  darkModeModalBackgroundColor,
  lightText,
} from "../../constants/colors";
import styled, { css } from "styled-components/macro";
import {
  NAV_HEIGHT_PX,
  Z_INDEX,
  BREAKPOINT_MUI_NAV_SHRINK_8_PX,
  BREAKPOINT_MOBILE,
  BREAKPOINT_TABLET,
  BREAKPOINT_DESKTOP_LG,
  BREAKPOINT_DESKTOP_XL,
  PNL_COLORS,
} from "../../constants/constants";
import LoadingIndicatorDashboardPages from "pages/DashboardPages/LoadingIndicatorDashboardPages";
import { useIsDarkMode, useWorkScheduleConfigJsonListAtom } from "store/atoms";
import TrendsPageDateRangeStartAndEnd from "./DateRange/TrendsPageDateRangeStartAndEnd";
import useWindowSize from "utils/customHooks/useWindowSize";
import { ViewingNumProcessesIndicator } from "./ViewingNumProcessesIndicator";
import { BREAKPOINT_DATERANGEPICKER_TWO_ROWS } from "./DateRange/dateRangeUtils";
import FilterStateDisplay from "./TrendsFilters/FilterStateDisplay";
import { MoreVert, ContentCopy } from "@mui/icons-material";
import { useState } from "react";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { darkBlue } from "reports/constants/colors";
import {
  useFocusRange,
  useTrendChartDataDumps,
  useTrendsPageFilterState,
  useTrendsPageFilteredRollups,
} from "utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { format } from "date-fns";
import {
  TrendsPageFilterStateType,
  WorkScheduleConfigJsonType,
} from "types/types";
import { CSVLink } from "react-csv";
import DownloadIcon from "@mui/icons-material/GetApp";
import { useLocation } from "react-router";
import { useCopyClipboard } from "@lokibai/react-use-copy-clipboard";
import { CopyLinkIcon } from "svgs";
import { isEqual, camelCase, startCase } from "lodash";
import {
  parseBreakdownToCSVRow,
  CSVRow,
} from "components/SimpleComponents/CSVDownloadButton";
import { useTrendChartBarsArray } from "utils/customHooks/trendChartHooks";
import { usePnlSummaryChartSections } from "utils/customHooks/pnlChartHooks";
import { addRollupValues } from "utils/metrics";
import { PnlCategoriesEnum } from "types/enums";
import { BREAKPOINT_DESKTOP } from "reports/constants/constants";
import { TimeZoneController } from "components/Settings/TimeZoneController";

export const DividerStyled = ({ style = {}, ...props }) => (
  <Divider
    style={{
      background: `${darkGrey} !important`,
      ...style,
    }}
    {...props}
  />
);

const TrendsPageTopBarStyles = styled.div<{ $isDarkMode: boolean }>`
  height: ${NAV_HEIGHT_PX + 5}px;
  .toolbar {
    position: fixed;
    top: ${NAV_HEIGHT_PX - 5}px;
    height: ${NAV_HEIGHT_PX}px;
    left: 0px;
    right: 0px;
    box-shadow: 0px 2px 5px #00000040;
    z-index: ${Z_INDEX.L11};
    background-color: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeModalBackgroundColor : lightText};
    overflow: visible;
    display: flex;
    padding: 0 8px;
    box-sizing: border-box;
    width: 100vw;
    .rightSide,
    .middle {
      flex-grow: 0;
    }
    .leftSide {
      flex-grow: 1;
      padding-left: 1em;
      @media (min-width: ${BREAKPOINT_DESKTOP_LG}px) {
        padding-left: 0;
      }
    }
    .leftSide {
      display: flex;
      align-items: center;
    }
    .filterStateDisplay {
      flex-grow: 1;
      max-width: 640px;
      .filtersWrapper {
        max-width: calc(100% - 42px);
        margin: 0;
        width: fit-content;
        margin-left: 28px;
      }
    }
    @media (min-width: ${BREAKPOINT_DESKTOP}px) {
      .rightSide {
        margin-top: 6px;
      }
    }
    @media (min-width: ${BREAKPOINT_DESKTOP_LG}px) and (max-width: ${1500}px) {
      [class*="ViewingNumProcessesStyles"] {
        max-width: 80px;
        padding: 0;
        font-size: 16px;
      }
    }
    @media (min-width: ${1500}px) {
      justify-content: end;
      .middle {
        position: absolute;
        left: 0;
        right: 0;
        width: fit-content;
        margin: auto;
      }
    }
    .rightSide {
      display: flex;
    }
  }
  /* switch to vertical two-row layout */
  @media (max-width: ${BREAKPOINT_DATERANGEPICKER_TWO_ROWS}px) {
    height: ${NAV_HEIGHT_PX + 24}px;
    .toolbar {
      height: ${NAV_HEIGHT_PX + 24}px;
      top: 56px;
    }
  }
  @media (min-width: ${BREAKPOINT_MUI_NAV_SHRINK_8_PX}px) {
    height: ${NAV_HEIGHT_PX}px;
    .toolbar {
      top: ${NAV_HEIGHT_PX}px;
    }
  }
  @media (min-width: ${BREAKPOINT_DESKTOP_XL}px) {
    .toolbar {
      top: ${NAV_HEIGHT_PX * 1.5}px;
    }
  }
`;

const TrendsPageTopBar = () => {
  // const handleOpenFiltersDrawer = () => setIsFiltersDrawerOpen(true);
  const [isDarkMode] = useIsDarkMode();
  const { innerWidth } = useWindowSize();
  const isDesktopLgOrLarger = useMediaQuery(
    `(min-width: ${BREAKPOINT_DESKTOP_LG}px)`
  );

  return (
    <>
      <TrendsPageTopBarStyles
        data-component-name={"Trends Page Top Bar"}
        $isDarkMode={isDarkMode}
      >
        <Toolbar className="toolbar">
          {innerWidth < BREAKPOINT_MOBILE ? (
            <div className="middle">
              <TrendsPageDateRangeStartAndEnd />
            </div>
          ) : BREAKPOINT_MOBILE <= innerWidth &&
            innerWidth < BREAKPOINT_DESKTOP_LG ? (
            <>
              <div className="leftSide">
                {innerWidth > BREAKPOINT_TABLET && (
                  <ViewingNumProcessesIndicator />
                )}
              </div>
              <div className="middle"></div>
              <div className="rightSide">
                <TrendsPageDateRangeStartAndEnd />
                <MoreMenu />
              </div>
            </>
          ) : (
            <>
              <div className="leftSide">
                {isDesktopLgOrLarger && (
                  <div className="filterStateDisplay">
                    <FilterStateDisplay />
                  </div>
                )}
                {innerWidth <= 1500 && <ViewingNumProcessesIndicator />}
              </div>
              <div className="middle">
                {innerWidth > 1500 && <ViewingNumProcessesIndicator />}
              </div>
              <div className="rightSide">
                <TrendsPageDateRangeStartAndEnd />
                <MoreMenu />
              </div>
            </>
          )}
        </Toolbar>
        <DividerStyled className="trendsPageTopBarDivider" />
        <LoadingIndicatorDashboardPages />
      </TrendsPageTopBarStyles>
    </>
  );
};
export default TrendsPageTopBar;

function MoreMenu() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  function handleClose() {
    setAnchorEl(null);
  }
  return (
    <div>
      <IconButton
        style={{ width: 48, height: 48 }}
        onClick={event => setAnchorEl(event.currentTarget)}
      >
        <MoreVert />
      </IconButton>
      <Menu
        id="menu-trends-topbar"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableScrollLock={true}
      >
        <TimeZoneControllerMenuItem />

        <TrendsPageCopyUrlMenuItem
          handleClose={() => {
            setTimeout(() => {
              handleClose();
            }, 500);
          }}
        />
        <TrendsPageCSVDownloadMenuItem {...{ handleClose }} />
        <TrendsPageCSVCopyClipboardMenuItem1 {...{ handleClose }} />
        <TrendsPageCSVCopyClipboardMenuItem2 {...{ handleClose }} />
      </Menu>
    </div>
  );
}
function TimeZoneControllerMenuItem() {
  return (
    <StyledMenuItem style={{ padding: "6px 14px", height: "fit-content" }}>
      <TimeZoneController
        customCss={css`
          gap: 8px;
        `}
      />
    </StyledMenuItem>
  );
}
function TrendsPageCopyUrlMenuItem({
  handleClose,
}: {
  handleClose: () => void;
}) {
  const [copiedValue, setCopiedValue] = useState(null as string | null);
  const { pathname, search } = useLocation();
  const pathToShare = `${window.location.origin}${pathname}${search}`;
  const isCopiedValueSameAsCurrentValue = copiedValue === pathToShare;

  const [, setCopied] = useCopyClipboard(pathToShare);

  // copy share URL to clipboard
  const handleCopyClipboard = () => {
    setCopied(pathToShare);
    setCopiedValue(pathToShare);
    setTimeout(() => setCopiedValue(null), 2500);
  };
  const copied = isCopiedValueSameAsCurrentValue;

  const { translate } = useFlightTranslation();

  return (
    <StyledMenuItem
      onClick={() => {
        handleCopyClipboard();
        handleClose();
      }}
    >
      <div>
        <IconButton>
          <CopyLinkIcon />
        </IconButton>
      </div>

      <div style={{ marginRight: 8 }}>
        {copied ? translate("Copied") + "!" : translate("Copy_url")}
      </div>
    </StyledMenuItem>
  );
}

function TrendsPageCSVDownloadMenuItem({
  handleClose,
}: {
  handleClose: () => void;
}) {
  const focusRange = useFocusRange();
  const { translate } = useFlightTranslation();

  const trendsPageCsvData = useTrendsPageCsvData();

  if (!focusRange || !trendsPageCsvData) {
    return null;
  }
  const {
    fileName,
    overallAndTrendsCsvData,
    pnlBreakdownCsvData,
  }: {
    fileName: string;
    overallAndTrendsCsvData: CSVRow[];
    pnlBreakdownCsvData: CSVRow[];
  } = trendsPageCsvData;

  return (
    <>
      <CSVLink
        filename={`Trends_Overall_${fileName}`}
        data={overallAndTrendsCsvData}
        style={{ color: "black", textDecoration: "none" }}
      >
        <StyledMenuItem onClick={handleClose}>
          <div>
            <IconButton>
              <DownloadIcon />
            </IconButton>
          </div>
          <div style={{ marginRight: 8 }}>
            {translate("Download_CSV")}: {translate("Overall_+_Trends")}
          </div>
        </StyledMenuItem>
      </CSVLink>
      <CSVLink
        filename={`Trends_PnL_${fileName}`}
        data={pnlBreakdownCsvData}
        style={{ color: "black", textDecoration: "none" }}
      >
        <StyledMenuItem onClick={handleClose}>
          <div>
            <IconButton>
              <DownloadIcon />
            </IconButton>
          </div>
          <div style={{ marginRight: 8 }}>
            {translate("Download_CSV")}: {translate("P&L Breakdown")}
          </div>
        </StyledMenuItem>
      </CSVLink>
    </>
  );
}

const StyledMenuItem = styled(MenuItem)`
  height: 40px;
  padding: 0px 12px 0 6px;
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 8px;
  .MuiSvgIcon-root {
    color: ${darkBlue} !important;
    fill: ${darkBlue} !important;
  }
`;

export function useTrendsPageCsvData() {
  const ckey = useCustomerKey();
  const focusRange = useFocusRange();
  const { trendsPageFilterState: state } = useTrendsPageFilterState();
  const [workSchedules] = useWorkScheduleConfigJsonListAtom();
  const trendsPageData = useTrendsPageFilteredRollups();
  const trendBars = useTrendChartBarsArray();
  const dataDumpsTrendChart = useTrendChartDataDumps();
  const pnlBreakdownChartCategorySectionsArr = usePnlSummaryChartSections({
    isPopup: false,
    isSubordinateProcessesOnly: false,
  });

  const overallTrendsData = addRollupValues(trendsPageData);

  const overallAndTrendsCsvData = [
    parseBreakdownToCSVRow(
      {
        rangeStart: trendsPageData[0]?.range.ge,
        rangeEnd: trendsPageData[trendsPageData.length - 1]?.range.lt,
        ...overallTrendsData,
      },
      "OVERALL"
    ),
  ];
  // const trendBarCounts =
  trendBars.forEach((bar, idx) => {
    overallAndTrendsCsvData.push(
      parseBreakdownToCSVRow(
        {
          rangeStart: bar.range.ge,
          rangeEnd: bar.range.lt,
          ...bar,
          count: dataDumpsTrendChart[idx]?.netOutput ?? null,
        },
        `BAR_${idx + 1}`
      )
    );
  });
  if (!focusRange) {
    return null;
  }
  const { ge, lt } = focusRange;
  const csvTitle = titleFromFilterState(state, workSchedules);
  const fileName = `${startCase(ckey)}__${csvTitle}${format(
    ge,
    "yyyy_MM_dd"
  )}-${format(lt, "yyyy_MM_dd")}`;

  let pnlBreakdownCsvData: CSVRow[] = [];
  pnlBreakdownChartCategorySectionsArr.forEach(section => {
    pnlBreakdownCsvData.push(
      parseBreakdownToCSVRow(
        {
          ...section,
          category: section.bars[0]?.category ?? camelCase(section.title),
          color: section.bars[0]?.color ?? PNL_COLORS[camelCase(section.title)],
        },
        section.title + "_total"
      )
    );

    // sections [unknown, notScheduled, uncollected] often have a single tag row identical to the category totel => ignore these tags
    const isTagRowRepresentingEntireCategory =
      section.bars.length === 1 &&
      section.bars[0].category &&
      [
        PnlCategoriesEnum.unknown,
        PnlCategoriesEnum.uncollected,
        PnlCategoriesEnum.notScheduled,
      ].includes(section.bars[0].category as PnlCategoriesEnum);

    // otherwise add a csv row for each tag
    if (!isTagRowRepresentingEntireCategory) {
      section.bars.forEach(bar => {
        pnlBreakdownCsvData.push(
          parseBreakdownToCSVRow(bar, `${section.title}_tag`)
        );
      });
    }
  });
  pnlBreakdownCsvData.sort((a, b) => (b.time ?? 0) - (a.time ?? 0));

  // after sorting, add suffix "_${idx}" to each row ending with "_tag", and restart the idx at 1 when the category changes (e.g. Not Scheduled_tag_1, Not Scheduled_tag_2, Planned_tag_1, Not Scheduled_tag_3, Planned_tag_2)
  const idxByCategory = {
    notScheduled: 1,
    producing: 1,
    planned: 1,
    unplanned: 1,
    waiting: 1,
    unknown: 1,
  };

  pnlBreakdownCsvData = pnlBreakdownCsvData.map(row => {
    if (!row.type?.endsWith("_tag")) {
      return row;
    }
    return {
      ...row,
      type: `${row.type ?? ""}_${idxByCategory[row.category ?? ""]++}`,
    };
  });
  return { fileName, overallAndTrendsCsvData, pnlBreakdownCsvData };
}

function TrendsPageCSVCopyClipboardMenuItem1({
  handleClose,
}: {
  handleClose: () => void;
}) {
  const { translate } = useFlightTranslation();
  const [copiedValue, setCopiedValue] = useState(null as any);

  const [, setCopied] = useCopyClipboard(null);

  const trendsPageCsvData = useTrendsPageCsvData();

  if (!trendsPageCsvData) {
    return null;
  }

  const csvStringFromDataRows =
    trendsPageCsvData.overallAndTrendsCsvData.reduce(
      (acc, row) => {
        const rowString = Object.values(row).join(",");
        return acc + rowString + "\n";
      },
      COLUMN_NAMES_OVERALL_CSV.join(",") + "\n"
    );

  const handleCopyClipboard = () => {
    setCopied(csvStringFromDataRows);
    setCopiedValue(csvStringFromDataRows);
    setTimeout(() => setCopiedValue(null), 2500);
  };

  const copied = isEqual(csvStringFromDataRows, copiedValue);

  return (
    <StyledMenuItem
      onClick={() => {
        handleCopyClipboard();
        handleClose();
      }}
    >
      <div>
        <IconButton>
          <ContentCopy />
        </IconButton>
      </div>
      <div style={{ marginRight: 8 }}>
        {copied
          ? translate("Copied") + "!"
          : `${translate("Copy_CSV")}: ${translate("Overall_+_Trends")}`}
      </div>
    </StyledMenuItem>
  );
}
const COLUMN_NAMES_PNL_CSV = [
  "type",
  "title",
  "time",
  "percent",
  "category",
  "color",
  "outputLoss",
  "numberOfEvents",
  "averageDurationMs",
];
const COLUMN_NAMES_OVERALL_CSV = [
  "type",
  "rangeStart",
  "rangeEnd",
  "notScheduled",
  "planned",
  "producing",
  "uncollected",
  "unknown",
  "unplanned",
  "waiting",
  "totalDurationMs",
];
function TrendsPageCSVCopyClipboardMenuItem2({
  handleClose,
}: {
  handleClose: () => void;
}) {
  const { translate } = useFlightTranslation();
  const [copiedValue, setCopiedValue] = useState(null as any);

  const [, setCopied] = useCopyClipboard(null);

  const trendsPageCsvData = useTrendsPageCsvData();

  if (!trendsPageCsvData) {
    return null;
  }

  const csvStringFromDataRows = trendsPageCsvData.pnlBreakdownCsvData.reduce(
    (acc, row) => {
      const rowString = Object.values(row).join(",");
      return acc + rowString + "\n";
    },
    COLUMN_NAMES_PNL_CSV.join(",") + "\n"
  );

  const handleCopyClipboard = () => {
    setCopied(csvStringFromDataRows);
    setCopiedValue(csvStringFromDataRows);
    setTimeout(() => setCopiedValue(null), 2500);
  };

  const copied = isEqual(csvStringFromDataRows, copiedValue);

  return (
    <StyledMenuItem
      onClick={() => {
        handleCopyClipboard();
        handleClose();
      }}
    >
      <div>
        <IconButton>
          <ContentCopy />
        </IconButton>
      </div>
      <div style={{ marginRight: 8 }}>
        {copied
          ? translate("Copied") + "!"
          : `${translate("Copy_CSV")}: ${translate("P&L Breakdown")}`}
      </div>
    </StyledMenuItem>
  );
}

function titleFromFilterState(
  state: TrendsPageFilterStateType,
  workSchedules: WorkScheduleConfigJsonType[]
): string {
  let title = "";

  if (state.group !== "All") {
    title += `Group_${state.group}__`;
  }
  if (state.process.id !== "All") {
    title += `Process_${state.process.name}__`;
  }
  if (state.tag !== "All") {
    title += `Tag_${state.tag}__`;
  }
  if (state.pnlCategory !== "All") {
    title += `PnlCategory_${state.pnlCategory}__`;
  }
  if (state.shiftName !== "All") {
    title += `Shift_${state.shiftName}__`;
  }
  if (workSchedules.length > 1) {
    title += `WorkSchedule_${state.workScheduleId}__`;
  }

  return title;
}
